import './vehicles-of-the-day.css';

import moment from 'moment';
import React, { useEffect, useRef } from 'react';

import useGetEligibleVehicles from '../../api/useGetEligibleVehicles';
import { COLORS, getTruckTitle } from './truck-assignments.utils';
import { ELIGIBILITY } from './vehicles-of-the-day.utils';

/** Un camion de GET /vehicles/eligible, au format des camions de l'écran. */
const toVehicle = (vehicle, status) => ({
  id: vehicle.vehicule_id,
  key: vehicle.vehicule_key,
  label: vehicle.label || null,
  licensePlate: vehicle.imat || null,
  color: vehicle.color || COLORS.primary,
  status,
  reason: vehicle.reason || null,
  createdBy: vehicle.created_by
    ? [vehicle.created_by.first_name, vehicle.created_by.last_name]
        .filter(Boolean)
        .join(' ')
    : null,
});

/**
 * Les camions non archivés de la date, rangés par clé, chacun avec son état
 */
const describeRows = (eligibility) =>
  eligibility
    ? [
        ...eligibility.eligible.map((v) => toVehicle(v, ELIGIBILITY.ELIGIBLE)),
        ...eligibility.unavailable.map((v) =>
          toVehicle(v, ELIGIBILITY.UNAVAILABLE)
        ),
        ...eligibility.disabled.map((v) => toVehicle(v, ELIGIBILITY.DISABLED)),
      ].sort((a, b) => String(a.key).localeCompare(String(b.key)))
    : [];

const describeSubtitle = (row, assignedCount) => {
  if (row.status === ELIGIBILITY.DISABLED) {
    return "Retiré de l'affectation depuis sa fiche véhicule";
  }
  const parts = [];
  if (row.status === ELIGIBILITY.UNAVAILABLE) {
    parts.push(row.reason ? `Indisponible · ${row.reason}` : 'Indisponible');
    if (row.createdBy) parts.push(`par ${row.createdBy}`);
    if (assignedCount > 0) {
      parts.push(
        `${assignedCount} commande${assignedCount > 1 ? 's' : ''} à déplacer`
      );
    }
  } else if (assignedCount > 0) {
    parts.push(
      `${assignedCount} commande${assignedCount > 1 ? 's' : ''} affectée${
        assignedCount > 1 ? 's' : ''
      }`
    );
  }
  return parts.join(' · ');
};

/**
 * Panneau latéral « Véhicules du jour » (aligné sur vehicles-panel.js de
 * tms-chargeur, vue Jour) : tous les camions sont disponibles par défaut ;
 * décocher un camion le rend indisponible ce jour (confirmation avec motif
 * facultatif et fin facultative), le recocher lève l'indisponibilité. Les
 * jours passés ne se modifient pas.
 *
 * @param {string} props.date - La date de l'écran (YYYY-MM-DD)
 * @param {object} props.truckCounts - Commandes affectées par id de camion
 * @param {*} props.refreshKey - Change à chaque modification de disponibilité
 * @param {boolean} props.isBusy - Une écriture est en cours
 * @param {Function} props.onMarkUnavailable - (vehicle) : ouvre la confirmation
 * @param {Function} props.onMakeAvailable - (vehicle) : lève l'indisponibilité du jour
 * @param {Function} props.onOpenVehicle - (vehicle) : ouvre la fiche véhicule
 */
export default function VehiclesOfTheDayPanel({
  date,
  truckCounts,
  refreshKey,
  isBusy,
  onClose,
  onMarkUnavailable,
  onMakeAvailable,
  onOpenVehicle,
}) {
  const { byDate, isLoading } = useGetEligibleVehicles({
    dates: [date],
    refreshKey,
  });
  const eligibility = byDate[date];
  const rows = describeRows(eligibility);
  const isPast = date < moment().format('YYYY-MM-DD');
  const unavailableCount = rows.filter(
    (row) => row.status !== ELIGIBILITY.ELIGIBLE
  ).length;

  // Échap ferme le panneau, sauf quand la confirmation est ouverte par-dessus
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (document.querySelector('.vod-modal')) return;
      onCloseRef.current?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <>
      <div className="vod-veil" onClick={onClose} />
      <aside className="vod-panel" role="dialog" aria-label="Véhicules du jour">
        <div className="vod-panel__head">
          <span className="vod-panel__title">Véhicules du jour</span>
          <span className="vod-panel__date">
            {moment(date, 'YYYY-MM-DD').format('ddd D MMM YYYY')}
          </span>
          <span className="ta-spacer" />
          <button
            type="button"
            className="mam__close"
            onClick={onClose}
            title="Fermer"
          >
            ✕
          </button>
        </div>
        <p className="vod-panel__hint">
          {isPast
            ? 'Jour passé : la disponibilité des camions ne se modifie plus.'
            : 'Coché : proposé à l’affectation et à l’optimisation ce jour. Décocher rend le camion indisponible ce jour (ou sur une période) ; ses commandes déjà affectées restent en place.'}
        </p>

        <div className="vod-panel__list">
          {!eligibility && (
            <p className="vod-panel__empty">
              {isLoading
                ? 'Chargement des véhicules…'
                : 'Les véhicules du jour n’ont pas pu être lus.'}
            </p>
          )}
          {eligibility && rows.length === 0 && (
            <p className="vod-panel__empty">Aucun véhicule.</p>
          )}
          {rows.map((row) => {
            const isUnavailable = row.status === ELIGIBILITY.UNAVAILABLE;
            const isDisabled = row.status === ELIGIBILITY.DISABLED;
            const assignedCount = truckCounts?.[row.id] || 0;
            const subtitle = describeSubtitle(row, assignedCount);
            // Un camion qui porte des commandes ce jour (ou envoyé, qui en
            // porte forcément) ne se rend pas indisponible : l'API refuse
            const hasAssignments =
              row.status === ELIGIBILITY.ELIGIBLE && assignedCount > 0;

            return (
              <div
                key={row.key}
                className={`vod-row ${
                  isUnavailable ? 'vod-row--unavailable' : ''
                } ${isDisabled ? 'vod-row--disabled' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={row.status === ELIGIBILITY.ELIGIBLE}
                  disabled={isDisabled || isPast || isBusy || hasAssignments}
                  onChange={() =>
                    isUnavailable
                      ? onMakeAvailable(row)
                      : onMarkUnavailable(row)
                  }
                  aria-label={
                    isUnavailable
                      ? `Rendre ${row.key} disponible ce jour`
                      : `Rendre ${row.key} indisponible ce jour`
                  }
                  title={
                    isDisabled
                      ? 'Retiré de l’affectation depuis sa fiche véhicule.'
                      : isPast
                        ? 'Jour passé : verrouillé.'
                        : hasAssignments
                          ? 'Déplacez d’abord ses commandes vers un autre camion.'
                          : undefined
                  }
                />
                <span
                  className="vod-row__dot"
                  style={{
                    backgroundColor:
                      isUnavailable || isDisabled ? '#c4c9cf' : row.color,
                  }}
                />
                <span className="vod-row__main">
                  <span className="vod-row__name">{getTruckTitle(row)}</span>
                  {subtitle && <span className="vod-row__sub">{subtitle}</span>}
                </span>
                {isUnavailable && (
                  <button
                    type="button"
                    className="ta-link"
                    onClick={() => onMakeAvailable(row)}
                    disabled={isPast || isBusy}
                  >
                    Rendre disponible
                  </button>
                )}
                {isDisabled && (
                  <button
                    type="button"
                    className="ta-link"
                    onClick={() => onOpenVehicle(row)}
                  >
                    Fiche ›
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="vod-panel__foot">
          {eligibility
            ? `${rows.length - unavailableCount} disponible${
                rows.length - unavailableCount > 1 ? 's' : ''
              } · ${unavailableCount} indisponible${
                unavailableCount > 1 ? 's' : ''
              } ce jour`
            : ''}
        </div>
      </aside>
    </>
  );
}
